// @ts-ignore
import {
  blue,
  blueCarbon,
  gray,
  grayCarbon,
  green,
  greenCarbon,
  red,
  redCarbon,
  yellow,
  yellowCarbon,
} from '@skcript/founderui-colors'
import type * as Stitches from '@stitches/react'
import { createStitches } from '@stitches/react'

export type { VariantProps } from '@stitches/react'

export const {
  styled,
  getCssText,
  css,
  globalCss,
  config,
  createTheme,
  keyframes,
} = createStitches({
  theme: {
    colors: {
      ...gray,
      ...blue,
      ...red,
      ...yellow,
      ...green,
      violet: '#CAB1F9',
      white: '#FFFFFF',
    },
    fonts: {
      base: 'Inter var, "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
      mono: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    },
    fontSizes: {
      1: '0.6875rem',
      2: '0.8125rem',
      3: '15px',
      4: '17px',
      5: '19px',
      6: '23px',
      7: '27px',
      8: '35px',
      9: '65px',
      10: '2.25rem',
    },
    space: {
      1: '5px',
      2: '10px',
      3: '15px',
      4: '20px',
      5: '25px',
      6: '35px',
      7: '45px',
      8: '65px',
      9: '80px',
      10: '130px',
    },
    sizes: {
      1: '5px',
      2: '10px',
      3: '15px',
      4: '20px',
      5: '25px',
      6: '35px',
      7: '45px',
      8: '65px',
      9: '80px',
      container: '1280px',
    },
    lineHeights: {
      1: '1',
      2: '1.25',
      3: '1.5',
    },
    radii: {
      1: '4px',
      2: '6px',
      3: '8px',
      4: '12px',
      5: '20px',
      round: '50%',
      full: '9999px',
    },
    transitions: {
      fast: '0.15s',
      normal: '0.3s',
      slow: '0.5s',
      // Custom
      hover: '0.2s',
    },
    shadows: {
      1: '0 0 0 1px rgba(0, 0, 0, 0.05)',
      2: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      3: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      4: '0 1px 4px 0 rgba(0, 0, 0, 0.1), 0 2px 2px 0 rgba(0, 0, 0, 0.05)',
      5: '0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 4px 5px 0 rgba(0, 0, 0, 0.04), 0 1px 10px 0 rgba(0, 0, 0, 0.02)',
      6: '0 3px 4px -2px rgba(0, 0, 0, 0.1), 0 6px 6px 0 rgba(0, 0, 0, 0.04), 0 1px 18px 0 rgba(0, 0, 0, 0.02)',
      7: '0 4px 5px -2px rgba(0, 0, 0, 0.1), 0 7px 10px 1px rgba(0, 0, 0, 0.04), 0 2px 16px 1px rgba(0, 0, 0, 0.02)',
      8: '0 5px 5px -3px rgba(0, 0, 0, 0.1), 0 8px 10px 1px rgba(0, 0, 0, 0.04), 0 3px 14px 2px rgba(0, 0, 0, 0.02)',
      9: '0 5px 6px -3px rgba(0, 0, 0, 0.1), 0 9px 12px 1px rgba(0, 0, 0, 0.04), 0 3px 16px 2px rgba(0, 0, 0, 0.02)',
    },
  },
  media: {
    bp1: '(min-width: 520px)',
    bp2: '(min-width: 900px)',
    bp3: '(min-width: 1200px)',
    bp4: '(min-width: 1800px)',
    motion: '(prefers-reduced-motion)',
    hover: '(any-hover: hover)',
  },
  utils: {
    // Abbreviated margin properties
    m: (value: any) => ({
      margin: value,
    }),
    mt: (value: any) => ({
      marginTop: value,
    }),
    mr: (value: any) => ({
      marginRight: value,
    }),
    mb: (value: any) => ({
      marginBottom: value,
    }),
    ml: (value: any) => ({
      marginLeft: value,
    }),
    mx: (value: any) => ({
      marginLeft: value,
      marginRight: value,
    }),
    my: (value: any) => ({
      marginTop: value,
      marginBottom: value,
    }),

    // Abbreviated padding properties
    p: (value: any) => ({
      padding: value,
    }),
    pt: (value: any) => ({
      paddingTop: value,
    }),
    pr: (value: any) => ({
      paddingRight: value,
    }),
    pb: (value: any) => ({
      paddingBottom: value,
    }),
    pl: (value: any) => ({
      paddingLeft: value,
    }),
    px: (value: any) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    py: (value: any) => ({
      paddingTop: value,
      paddingBottom: value,
    }),

    // Abbreviated border properties
    b: (value: any) => ({
      border: value,
    }),
    bt: (value: any) => ({
      borderTop: value,
    }),
    br: (value: any) => ({
      borderRight: value,
    }),
    bb: (value: any) => ({
      borderBottom: value,
    }),
    bl: (value: any) => ({
      borderLeft: value,
    }),
    bx: (value: any) => ({
      borderLeft: value,
      borderRight: value,
    }),
    by: (value: any) => ({
      borderTop: value,
      borderBottom: value,
    }),

    // A property for applying width/height together
    size: (value: any) => ({
      width: value,
      height: value,
    }),

    // A property to apply linear gradient
    linearGradient: (value: any) => ({
      backgroundImage: `linear-gradient(${value})`,
    }),

    // An abbreviated property for border-radius
    brd: (value: any) => ({
      borderRadius: value,
    }),
  },
})

export type CSS = Stitches.CSS<typeof config>

// @ts-ignore
export const darkTheme = createTheme('dark-theme', {
  colors: {
    ...grayCarbon,
    ...blueCarbon,
    ...redCarbon,
    ...yellowCarbon,
    ...greenCarbon,
    violet: '#CAB1F9',
    white: '#FFFFFF',
  },
})
